import { useState } from 'react';
import type { ConsignmentFeePolicy, ProposedConsignmentFeeSchedule } from '@tcvs/shared';
import { formatMoneyMinor } from './consignment-ui';

const feeFields = [
  ['soldSingleCardFeeMinor', 'Sold single card fee'],
  ['soldSealedWaxFeeMinor', 'Sold sealed wax fee'],
  ['unsoldItemFeeMinor', 'Unsold item fee'],
  ['expressSurchargeMinor', 'Express service surcharge'],
  ['premiumSurchargeMinor', 'Premium service surcharge'],
] as const;

export function FeeProposalSummary({
  schedule,
}: {
  schedule: ProposedConsignmentFeeSchedule | ConsignmentFeePolicy;
}) {
  return (
    <div>
      <strong>
        {schedule.kind === 'FULL_SCHEDULE_V1' ? 'Tiered payout terms' : 'Proposed payout terms'}
      </strong>
      <p>
        Whole sale price selects the payout tier. Listing and selected service fees are deducted
        from that share. Buyer tax, shipping and actual marketplace fees are not additional
        deductions.
      </p>
      <ul>
        {schedule.tiers.map((tier, index) => (
          <li key={index}>
            {formatMoneyMinor(tier.minimumGrossMinor)}
            {schedule.tiers[index + 1]
              ? `–${formatMoneyMinor(schedule.tiers[index + 1]!.minimumGrossMinor - 1)}`
              : '+'}
            : {(tier.payoutBps / 100).toFixed(2)}% payout
          </li>
        ))}
      </ul>
      <p>Amounts below the first tier are not covered by this proposal.</p>
      <ul>
        {feeFields.map(([key, label]) => (
          <li key={key}>
            {label}: {formatMoneyMinor(schedule[key])}
          </li>
        ))}
      </ul>
      <p>
        Item type: {schedule.selectedItemType ?? 'Not agreed'} · Service:{' '}
        {schedule.selectedService ?? 'Not agreed'} · Zero payout floor:{' '}
        {schedule.zeroProceedsFloor === null
          ? 'Not agreed'
          : schedule.zeroProceedsFloor
            ? '$0 minimum per sold item'
            : 'Not proposed'}
      </p>
      <p>
        Confirmed unsold items: unsold fee plus listing and selected service fees. Negative
        statement balances require review; no automatic collection.
      </p>
      {schedule.kind === 'FULL_SCHEDULE_V1' && (
        <p>
          Refunds:{' '}
          {schedule.refundPolicy === 'ORIGINAL_TIER_REVERSE_FULL'
            ? 'Keep the original tier after partial refunds'
            : 'Recalculate the tier after partial refunds'}
          ; reverse all fees after a full refund. Sold-item deductions are capped at sale proceeds,
          applying commission, then listing, then service fees.
        </p>
      )}
    </div>
  );
}

function DecimalField({
  label,
  initial,
  onChange,
}: {
  label: string;
  initial: number;
  onChange: (minor: number) => void;
}) {
  const [value, setValue] = useState((initial / 100).toFixed(2));
  return (
    <label className="tcvs-field">
      <span>{label}</span>
      <input
        aria-label={label}
        inputMode="decimal"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          const [whole, fraction = ''] = e.target.value.split('.');
          const parsed = /^\d+(\.\d{1,2})?$/.test(e.target.value)
            ? Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
            : NaN;
          onChange(parsed);
        }}
      />
    </label>
  );
}

export function FeeProposalEditor({
  schedule,
  onChange,
}: {
  schedule: ProposedConsignmentFeeSchedule;
  onChange: (schedule: ProposedConsignmentFeeSchedule) => void;
}) {
  return (
    <div>
      <h4>Proposed payout tiers and fees</h4>
      <p>
        Payout is the consignor’s share before the proposed fees. Tiers start at the amount entered
        and end one cent below the next tier; the final tier has no upper limit. All amounts are
        USD.
      </p>
      {schedule.tiers.map((tier, index) => (
        <div className="tcvs-form-row" key={index}>
          <DecimalField
            label={`Tier ${index + 1} minimum sale USD`}
            initial={tier.minimumGrossMinor}
            onChange={(value) =>
              onChange({
                ...schedule,
                tiers: schedule.tiers.map((t, i) =>
                  i === index ? { ...t, minimumGrossMinor: value } : t,
                ),
              })
            }
          />
          <DecimalField
            label={`Tier ${index + 1} payout percentage`}
            initial={tier.payoutBps}
            onChange={(value) =>
              onChange({
                ...schedule,
                tiers: schedule.tiers.map((t, i) => (i === index ? { ...t, payoutBps: value } : t)),
              })
            }
          />
        </div>
      ))}
      {feeFields.map(([key, label]) => (
        <DecimalField
          key={key}
          label={`${label} USD`}
          initial={schedule[key]}
          onChange={(value) => onChange({ ...schedule, [key]: value })}
        />
      ))}
      <label className="tcvs-field">
        <span>Proposed item type</span>
        <select
          aria-label="Proposed item type"
          value={schedule.selectedItemType ?? ''}
          onChange={(e) =>
            onChange({
              ...schedule,
              selectedItemType:
                e.target.value === '' ? null : (e.target.value as 'SINGLE_CARD' | 'SEALED_WAX'),
            })
          }
        >
          <option value="">Not agreed</option>
          <option value="SINGLE_CARD">Single cards</option>
          <option value="SEALED_WAX" disabled>
            Sealed wax (separate item intake required)
          </option>
        </select>
      </label>
      <label className="tcvs-field">
        <span>Proposed service</span>
        <select
          aria-label="Proposed service"
          value={schedule.selectedService ?? ''}
          onChange={(e) =>
            onChange({
              ...schedule,
              selectedService:
                e.target.value === ''
                  ? null
                  : (e.target.value as 'STANDARD' | 'EXPRESS' | 'PREMIUM'),
            })
          }
        >
          <option value="">Not agreed</option>
          <option value="STANDARD">Standard</option>
          <option value="EXPRESS">Express</option>
          <option value="PREMIUM">Premium</option>
        </select>
      </label>
      <p>
        Sold-item payout has a $0 minimum. Confirmed unsold items incur the unsold fee plus listing
        and selected service fees.
      </p>
    </div>
  );
}
